import React, { useRef, useState, useEffect } from "react"
import { createUseStyles } from "react-jss"
import { Anchor } from "../app/components/anchor"

import * as config from "../util/config.ts"

import mobileLogo from "../assets/icons/cs-logo-mobile.svg"
import mobileWhiteLogo from "../assets/icons/logo-white.svg"
import desktopLogo from "../assets/icons/cs-logo-desktop.svg"
import desktopWhiteLogo from "../assets/icons/logo-white-with-text.svg"

import colors from "../styles/colors"
import { mediaQueries } from "../styles/breakpoints"
import { fontWeight, fontSize } from "../styles/fonts-constants"

const useStyles = createUseStyles({
  navbar: {
    position: "absolute",
    padding: 16,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: '"Montserrat", sans-serif',
    backgroundColor: "transparent",
    transition: "background-color 300ms ease, box-shadow 300ms ease",
    [mediaQueries.desktop]: {
      padding: "20px 16px",
    },
  },
  navbarFixed: {
    position: "fixed",
    backgroundColor: colors.white1,
    boxShadow: `0px 12px 20px ${colors.black6}`,
  },
  logo: {
    width: 40,
    flex: "0 0 40px",
    [mediaQueries.desktop]: {
      width: 198,
      flex: "0 0 198px",
    },
  },
  logoImage: {
    display: "block",
    width: "100%",
    height: "auto",
  },
  logoImageMobile: {
    [mediaQueries.desktop]: {
      display: "none",
    },
  },
  logoImageDesktop: {
    display: "none",
    [mediaQueries.desktop]: {
      display: "inline-flex",
    },
  },
  navigation: {
    position: "absolute",
    left: 0,
    top: "100%",
    width: "100%",
    padding: 0,
    boxShadow: "0px 15px 30px rgba(6, 17, 46, 0.08)",
    overflow: "hidden",
    background: "white",
    maxHeight: (props) => (props.navigationExpanded ? "600px" : "0px"),
    transition: "max-height 300ms ease",
    [mediaQueries.desktop]: {
      position: "relative",
      left: "auto",
      top: "auto",
      overflow: "visible",
      boxShadow: "none",
      maxHeight: () => 50,
      background: "transparent",
      width: "auto",
      paddingRight: 20,
    },
  },
  navbarNav: {
    borderTop: "1px solid #DEE3EF",
    borderBottom: "1px solid #DEE3EF",
    padding: "32px 0",
    margin: 0,
    [mediaQueries.desktop]: {
      borderTop: "none",
      borderBottom: "none",
      padding: 0,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",

      "& li": {
        marginLeft: 16,
      },
      "& $ctaLink": {
        display: "inline-flex",
      },
    },
    [mediaQueries.largeDesktop]: {
      "& li": {
        margin: "0 20px",
      },
      "& li:first-child": {
        marginRight: 0,
      },
      "& $outlinedLinkWrapper": {
        marginLeft: 20,
        marginRight: 0,
      },
    },
  },
  outlinedLinkWrapper: {
    marginBottom: 12,
    padding: "0 10px",
    [mediaQueries.desktop]: {
      marginBottom: 0,
      padding: 0,
    },
  },
  ctaLink: {
    color: `${colors.blue7}`,
    backgroundColor: colors.white1,
    lineHeight: "40px",
    padding: "0px 15px",
    border: `1px solid ${colors.blue7}`,
    borderRadius: 100,
    display: "block",
    fontSize: fontSize.small,
    fontWeight: fontWeight.bold,
    width: "100%",
    transition: "background-color 300ms ease, color 300ms ease",

    "&:hover": {
      backgroundColor: colors.blue7,
      color: `${colors.white1} !important`,
      textDecoration: "none",
    },
    [mediaQueries.desktop]: {
      fontWeight: fontWeight.extraBold,
      lineHeight: "50px",
      padding: "0px 25px",
      border: `1px solid ${colors.blue7}`,
      borderRadius: 100,
      maxWidth: 400,
    },
  },
  mobileCtaLink: {
    display: "inline-flex",
    alignItems: "center",
    fontWeight: fontWeight.extraBold,
    fontSize: fontSize.tiny,
    color: `${colors.blue7}`,
    backgroundColor: colors.white1,
    lineHeight: "40px",
    padding: "2px 8px",
    border: `1px solid ${colors.blue7}`,
    borderRadius: 100,

    "&:hover": {
      backgroundColor: colors.blue7,
      color: `${colors.white1} !important`,
    },
    [mediaQueries.desktop]: {
      display: "none",
    },
  },
  navLink: {
    display: "block",
    fontSize: fontSize.small,
    padding: "7px 16px",
    color: colors.gray9,
    fontWeight: fontWeight.bold,
    width: "100%",
    transition: "color 300ms ease",

    "&:hover": {
      backgroundColor: colors.blue24,
    },
    [mediaQueries.desktop]: {
      padding: 0,
      color: colors.yellow3,
      "&:hover": {
        color: colors.blue7,
        backgroundColor: "transparent",
      },
    },
  },
  navLinkWhite: {
    color: "white",
  },
  toggleNavigation: {
    background: colors.white1,
    border: `1px solid ${colors.gray2}`,
    boxSizing: "border-box",
    borderRadius: 19,
    padding: "9px 20px",
    color: `${colors.gray1} `,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",

    "&:focus, &:active": {
      outline: "none",
    },
    [mediaQueries.desktop]: {
      display: "none",
    },
  },
  toggleMore: {
    fontFamily: "'Roboto', sans-serif",
    fontSize: fontSize.small,
    fontWeight: fontWeight.bold,
    marginRight: 16,
  },
  hamburgerLines: {
    width: 18,
  },
  hamburgerLine: {
    display: "block",
    width: "100%",
    height: 3,
    backgroundColor: colors.gray6,
    borderRadius: 8,
    marginBottom: 2,
  },
})

const SimpleAppBar = (props) => {
  const { activeButton, sideButton, fixedNavigation, highlight = false } = props
  const appHost = config.getAppHost()

  const [navigationExpanded, setNavigationExpanded] = React.useState(false)
  const [isFixed, setIsFixed] = useState(false)
  const scrollPos = useRef(0)

  useEffect(() => {
    let onScroll = () => {
      scrollPos.current = window.pageYOffset
      if (fixedNavigation) {
        if (!isFixed && scrollPos.current > 0) {
          setIsFixed(true)
        } else if (isFixed && scrollPos.current === 0) {
          setIsFixed(false)
        }
      }
    }
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [isFixed])

  const classes = useStyles({ navigationExpanded })

  return (
    <header
      className={`${classes.navbar} ${isFixed ? classes.navbarFixed : ""}`}
    >
      <Anchor href="/" className={classes.logo}>
        <img
          className={`${classes.logoImage} ${classes.logoImageMobile}`}
          type="imaWhiteWithTextge/svg+xml"
          src={highlight ? mobileWhiteLogo.src : mobileLogo.src}
          alt="CitizenShipper logo mobile"
          height={highlight ? mobileWhiteLogo.height : mobileLogo.height}
          width={highlight ? mobileWhiteLogo.width : mobileLogo.width}
        />
        <img
          className={`${classes.logoImage} ${classes.logoImageDesktop}`}
          type="image/svg+xml"
          src={highlight ? desktopWhiteLogo.src : desktopLogo.src}
          height={highlight ? desktopWhiteLogo.height : desktopLogo.height}
          width={highlight ? desktopWhiteLogo.weight : desktopLogo.weight}
          alt="CitizenShipper logo desktop"
        />
      </Anchor>
      <button
        className={classes.toggleNavigation}
        onClick={() => {
          setNavigationExpanded((previous) => {
            return !previous
          })
        }}
      >
        <span className={classes.toggleMore}>More</span>
        <div className={classes.hamburgerLines}>
          <span className={classes.hamburgerLine}></span>
          <span className={classes.hamburgerLine}></span>
          <span className={classes.hamburgerLine}></span>
        </div>
      </button>
      <nav className={classes.navigation}>
        <ul className={classes.navbarNav}>
          {activeButton.map((outlinedLink, index) => {
            return outlinedLink.route ? (
              <li key={index} className={classes.outlinedLinkWrapper}>
                <Anchor href={outlinedLink.route} className={classes.ctaLink}>
                  {outlinedLink.text}
                </Anchor>
              </li>
            ) : (
              <li key={index} className={classes.outlinedLinkWrapper}>
                <span className={classes.ctaLink}>{outlinedLink.text}</span>
              </li>
            )
          })}
          {sideButton.map((link, index) => {
            const href = link.isReactLink
              ? `${appHost}${link.route}`
              : link.route
            return href ? (
              <li key={index}>
                <Anchor
                  href={href}
                  className={`${classes.navLink} ${
                    highlight ? classes.navLinkWhite : null
                  }`}
                >
                  {link.text}
                </Anchor>
              </li>
            ) : (
              <li key={index}>
                <span
                  className={`${classes.navLink} ${
                    highlight ? classes.navLinkWhite : null
                  }`}
                >
                  {link.text}
                </span>
              </li>
            )
          })}
        </ul>
      </nav>
    </header>
  )
}

SimpleAppBar.defaultProps = {
  activeButton: [{ text: "Become a driver", route: "/register" }],
  sideButton: [
    { text: "How it works", route: "/how-it-works", isReactLink: false },
    {
      text: "Log in",
      route: `/login`,
      isReactLink: true,
    },
  ],
  fixedNavigation: false,
}

export default SimpleAppBar
